import { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
} from '@lexical/selection';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { Dropdown, MenuItem } from 'react-bootstrap';

import { mergeRegister } from '@lexical/utils';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';

interface TextFontFamilyButtonProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const FONT_FAMILIES = [
  { label: 'Sans Serif', value: 'sans-serif' },
  { label: 'Serif', value: 'serif' },
  { label: 'Monospace', value: 'monospace' },
  { label: 'Cursive', value: 'cursive' },
  { label: 'Fantasy', value: 'fantasy' },
] as const;

const TextFontFamilyButton = ({
  disabled,
  toolbarSettings: { features },
}: TextFontFamilyButtonProps) => {
  const [editor] = useLexicalComposerContext();
  const [currentFontFamily, setCurrentFontFamily] = useState('sans-serif');

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const fontFamily = $getSelectionStyleValueForProperty(
        selection,
        'font-family',
        'sans-serif',
      );

      setCurrentFontFamily(fontFamily);
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  if (!features?.fontFamily) {
    return null;
  }

  const onFontFamilyClick = (fontFamily: unknown) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, {
          'font-family': fontFamily as string,
        });
      }
    });
  };

  const toggleLabel = FONT_FAMILIES.find(
    ({ value }) => currentFontFamily === value,
  )?.label;

  return (
    <Dropdown disabled={disabled} id="rich-text-editor-font-family">
      <Dropdown.Toggle
        className="toolbar-item toolbar-item--large toolbar-item--vertical-dropdown"
        data-testid="toolbar-button-fontFamily"
      >
        {toggleLabel ?? '-'}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {FONT_FAMILIES.map(({ label, value }) => (
          <MenuItem
            aria-label={label}
            eventKey={value}
            key={value}
            onSelect={onFontFamilyClick}
          >
            {label}
          </MenuItem>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TextFontFamilyButton;
